"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Header } from "@/components/header";
import { HeroSection } from "@/components/hero-section";
import Link from "next/link";
import { useState, useEffect } from "react";
import { useInView } from "@/hooks/useInView";
import { careerPositionsApi, departmentApi, contentInfoApi } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";

import dynamic from "next/dynamic";

// Lazy load Footer
const Footer = dynamic(() => import("@/components/footer").then(mod => ({ default: mod.Footer })), {
  loading: () => <div className="h-75 bg-gray-200 animate-pulse" />,
});

// Lazy load ChatWidget
const ChatWidget = dynamic(() => import("@/components/chat-widget"), {
  loading: () => null,
  ssr: false,
});

interface Career {
  id_career: string;
  id?: string;
  title: string;
  id_department?: string | null; // ✅ comes from backend now
  location: string;
  salary: string;
  description: string;
  responsibilities: string;
  requirements: string;
  qualifications: string;
  type: string;
  email: string;
  application_deadline: string;
  is_active: boolean;
  is_published?: boolean;
}

interface Department {
  id_department: string; // ✅ FIX: backend uses id_department
  name_department: string;
}

interface JobListing {
  id: string;
  title: string;
  tags: string[];
  location: string;
  dateRange: string;
  salary: string;
  description: string;
  responsibilities: string[];
  requirements: string[];
  qualifications: string[];
  type: string;
  deadline: string;
  email: string;
}

interface ContentInfo {
  id_content: string;
  content_title: string;
  content_description?: string | null;
  type?: string | null;
}

export default function Career() {
  const { language } = useLanguage();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("all");
  const [contentRef, contentInView] = useInView<HTMLDivElement>({ threshold: 0.1 });
  const [jobsRef, jobsInView] = useInView<HTMLDivElement>({ threshold: 0.1 });
  const [jobListings, setJobListings] = useState<JobListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [heroContent, setHeroContent] = useState<ContentInfo | null>(null);
  const [heroLoading, setHeroLoading] = useState(true);

  const ITEMS_PER_PAGE = 6;

  // Helper function to handle salary parsing - display as is from backend
  const formatSalaryDisplay = (salary: string): string => {
    const formattedSalary = salary.replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,").replace(/\./g, ",");
    return formattedSalary || "Competitive";
  };

  const labels = {
    en: {
      applyBefore: "Apply before",
      applyAsSoon: "Apply as soon as possible",
      findCareers: "Find career",
      allDepartments: "All Departments",
      showDetails: "Show Details",
    },
    id: {
      applyBefore: "Daftar sebelum",
      applyAsSoon: "Daftar sesegera mungkin",
      findCareers: "Cari karir",
      allDepartments: "Semua Departemen",
      showDetails: "Lihat Detail",
    },
  };

  const t = labels[language];

  // Fetch hero content from database
  useEffect(() => {
    let isMounted = true;

    const fetchHeroContent = async () => {
      try {
        const response = await contentInfoApi.getAll({ limit: 1, type: "career", lang: language });
        const data = (response.data?.data || response.data) as ContentInfo[];

        if (!isMounted) return;
        setHeroContent(data?.[0] || null);
      } catch (err) {
        if (isMounted) setHeroContent(null);
        console.log("Error fetching careers hero content:", err);
      } finally {
        if (isMounted) setHeroLoading(false);
      }
    };

    fetchHeroContent();
    return () => {
      isMounted = false;
    };
  }, [language]);

  // Fetch career data from API
  useEffect(() => {
    const fetchCareers = async () => {
      try {
        setLoading(true);
        setError(null);

        // Define labels inside useEffect to avoid dependency issues
        const labels = {
          en: {
            applyBefore: "Apply before",
            applyAsSoon: "Apply as soon as possible",
          },
          id: {
            applyBefore: "Daftar sebelum",
            applyAsSoon: "Daftar sesegera mungkin",
          },
        };

        const currentLabels = labels[language];

        // Fetch careers and departments in parallel
        const [careersRes, departmentsRes] = await Promise.all([
          careerPositionsApi.getAll({ limit: 100, is_active: true, lang: language }),
          departmentApi.getAll({ limit: 100 }),
        ]);

        const careers = (careersRes.data.data || careersRes.data) as Career[];
        const departments = (departmentsRes.data.data || departmentsRes.data) as Department[];

        // ✅ Department lookup map: id_department -> name_department
        const departmentMap = new Map(
          departments.map((dept) => [dept.id_department, dept.name_department])
        );

        const formatDate = (dateString: string | null | undefined) => {
          if (!dateString) return null;
          const date = new Date(dateString);
          if (Number.isNaN(date.getTime())) return null;
          const locale = language === "id" ? "id-ID" : "en-US";
          return date.toLocaleDateString(locale, {
            year: "numeric",
            month: "long",
            day: "numeric",
          });
        };

        const parseToArray = (text: string | null | undefined) => {
          if (!text) return [];
          return text
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean);
        };

        const transformedJobs: JobListing[] = careers.map((career) => {
          // ✅ Use id_department from backend
          const departmentId = career.id_department ?? null;
          const departmentName =
            departmentId ? departmentMap.get(departmentId) || "" : "";

          const applicationDeadline = formatDate(career.application_deadline);

          return {
            id: career.id_career || career.id || "",
            title: career.title || "",
            tags: departmentName ? [departmentName] : [],
            location: career.location || "Depok, Indonesia",
            dateRange: applicationDeadline || currentLabels.applyAsSoon,
            salary: career.salary || "Competitive",
            description: career.description || "",
            responsibilities: parseToArray(career.responsibilities),
            requirements: parseToArray(career.requirements),
            qualifications: parseToArray(career.qualifications),
            type: career.type || "Full Time",
            deadline: applicationDeadline
              ? `${currentLabels.applyBefore} ${applicationDeadline}`
              : currentLabels.applyAsSoon,
            email: career.email || "hr@ganeshait.com",
          };
        });

        setJobListings(transformedJobs);
      } catch (err) {
        console.log("Error fetching careers:", err);
        setError("Failed to load career positions");
        setJobListings([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCareers();
  }, [language]);

  // ✅ Reset to page 1 when filtering changes (prevents empty page)
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedDepartment]);

  // Get unique departments from all job tags
  const allDepartments = Array.from(
    new Set(
      jobListings
        .flatMap((job) => job.tags)
        .filter((tag) => tag && tag.trim() !== "")
    )
  ).sort();

  // Filter job listings based on search and department
  const filteredJobs = jobListings.filter((job) => {
    const q = searchQuery.trim().toLowerCase();

    const matchesSearch =
      q === "" ||
      job.title.toLowerCase().includes(q) ||
      job.type.toLowerCase().includes(q) ||
      job.location.toLowerCase().includes(q) ||
      job.description.toLowerCase().includes(q) ||
      job.salary.toLowerCase().includes(q) ||
      job.tags.some((tag) => tag.toLowerCase().includes(q));

    const matchesDepartment =
      selectedDepartment === "all" ||
      job.tags.some(
        (tag) => tag.trim().toLowerCase() === selectedDepartment.trim().toLowerCase()
      );

    return matchesSearch && matchesDepartment;
  });

  // Calculate pagination
  const totalPages = Math.ceil(filteredJobs.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentJobs = filteredJobs.slice(startIndex, endIndex);
  const pagesToShow: Array<number | "..."> = [];

  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pagesToShow.push(i);
  } else {
    if (currentPage <= 3) {
      pagesToShow.push(1, 2, 3, 4, "...", totalPages);
    } else if (currentPage >= totalPages - 2) {
      pagesToShow.push(1, "...", totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
    } else {
      pagesToShow.push(1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages);
    }
  }

  return (
    <div className="page-layout bg-white">
      <div className="page-content">
        <Header activePage="career" />

        <HeroSection
          title={heroLoading ? "" : heroContent?.content_title || "Our Careers"}
          subtitle={
            heroLoading
              ? ""
              : heroContent?.content_description || "Explore open roles and grow your career with our team."
          }
        />

        <section className="py-12 px-8 lg:px-16 bg-white">
          <div className="max-w-7xl mx-auto">
            <div
              ref={contentRef}
              className={`flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8 reveal ${contentInView ? "is-visible" : ""
                }`}
            >
              <div className="relative w-full md:w-auto">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-black">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
                <Input
                  type="text"
                  placeholder={t.findCareers}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 w-full md:w-64 bg-white"
                />
              </div>

              <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
                <SelectTrigger className="w-full md:w-48 bg-white border-gray-300">
                  <SelectValue placeholder={t.allDepartments} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t.allDepartments}</SelectItem>
                  {allDepartments.map((dept) => (
                    <SelectItem key={dept} value={dept}>
                      {dept}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div
              ref={jobsRef}
              className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12 reveal ${jobsInView ? "is-visible reveal-delay-200" : ""
                }`}
            >
              {loading && (
                <div className="col-span-full text-center py-12">
                  <p className="text-lg text-gray-600">Loading career positions...</p>
                </div>
              )}

              {error && (
                <div className="col-span-full text-center py-12">
                  <p className="text-lg text-red-600">{error}</p>
                </div>
              )}

              {!loading && !error && currentJobs.length > 0 ? (
                currentJobs.map((job) => (
                  <div
                    key={job.id}
                    className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 hover:shadow-md transition-shadow flex flex-col h-full"
                  >
                    <h2 className="text-lg font-bold text-black mb-4 line-clamp-2">{job.title}</h2>

                    <div className="flex flex-wrap gap-2 mb-4">
                      {job.tags.map((tag, idx) => (
                        <span
                          key={`${job.id}-tag-${idx}`}
                          className="px-3 py-1 text-xs border border-black rounded-full text-black bg-white hover:bg-gray-50 transition-colors"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>

                    <div className="space-y-3 mb-6">
                      <div className="flex items-center gap-2 text-sm text-black">
                        <svg
                          className="w-4 h-4 text-black"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 3l9 4.5-9 4.5-9-4.5L12 3z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 10.5v4.5c0 1.5 4 3 9 3s9-1.5 9-3v-4.5" />
                        </svg>
                        <span>{job.type}</span>
                      </div>

                      <div className="flex items-center gap-2 text-sm text-black">
                        <svg className="w-4 h-4 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                          />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span>{job.location}</span>
                      </div>

                      <div className="flex items-center gap-2 text-sm text-black">
                        <svg className="w-4 h-4 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>{job.dateRange}</span>
                      </div>

                      <div className="flex items-center gap-2 text-sm text-black">
                        <svg className="w-4 h-4 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <span className="text-[#285494] font-semibold">{formatSalaryDisplay(job.salary)}</span>
                      </div>

                      <div className="flex items-center gap-2 text-sm text-gray-500 italic">
                        <span>{job.deadline}</span>
                      </div>
                    </div>

                    <div className="mt-auto">
                      <Link href={`/careers/${job.id}`} className="w-full">
                        <Button
                          className="w-full rounded-[10px] px-6 py-2 text-white font-semibold transition-opacity hover:opacity-90"
                          style={{ background: "linear-gradient(135deg, #0C1A2E 0%, #285494 100%)" }}
                        >
                          {t.showDetails}
                        </Button>
                      </Link>
                    </div>
                  </div>
                ))
              ) : (
                !loading &&
                !error && (
                  <div className="col-span-full text-center py-12">
                    <p className="text-gray-500 text-lg">
                      {language === "id"
                        ? "Saat ini kami tidak memiliki lowongan pekerjaan. Nantikan kesempatan berikutnya!"
                        : "We don't have any job vacancies right now. Stay tuned for the next opportunity!"}
                    </p>
                  </div>
                )
              )}
            </div>

            <div className="flex justify-end items-center gap-2 mt-12">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 rounded-md border border-gray-300 text-black hover:border-[#0C1A2E] hover:text-[#0C1A2E] hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              {pagesToShow.map((page, idx) => (
                <button
                  key={`${page}-${idx}`}
                  onClick={() => typeof page === "number" && setCurrentPage(page)}
                  disabled={page === "..."}
                  className={`px-4 py-2 rounded-md border transition-colors ${page === currentPage
                    ? "bg-[#0C1A2E] text-white border-[#0C1A2E]"
                    : page === "..."
                      ? "border-gray-300 text-black cursor-default px-2"
                      : "border-gray-300 text-black hover:border-[#0C1A2E] hover:text-[#0C1A2E] hover:bg-gray-50"
                    }`}
                >
                  {page}
                </button>
              ))}

              <button
                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-2 rounded-md border border-gray-300 text-black hover:border-[#0C1A2E] hover:text-[#0C1A2E] hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </section>
      </div>

      <Footer />
      <ChatWidget />
    </div>
  );
}
